/**
 * 個別のビューアイテムコンポーネント（メモ化済み）
 * 不要な再レンダリングを防ぐため分離
 */

import Link from "next/link";
import React from "react";
import {
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import type { AnySidebarItem } from "./sortable-types";
import { isPathActive } from "./sortable-utils";

interface SidebarSortableItemProps {
  item: AnySidebarItem;
  href: string;
  pathname: string;
  isDragging: boolean;
  isReorderEnabled: boolean;
  className?: string;
  renderViewContent: (item: AnySidebarItem) => React.ReactNode;
}

/**
 * サイドバーのソート可能なアイテム（メモ化でパフォーマンス最適化）
 */
export const SidebarSortableItem = React.memo(
  function SidebarSortableItem({
    item,
    href,
    pathname,
    isDragging,
    className,
    renderViewContent,
    isReorderEnabled,
  }: SidebarSortableItemProps) {
    const isActive = isPathActive(pathname, href);

    return (
      <SidebarMenuSubItem
        className="w-full"
        style={{ width: "calc(100% + 11px)" }}
      >
        <SidebarMenuSubButton
          asChild
          isActive={isActive}
          className={cn(
            "w-full",
            className,
            isDragging && "pointer-events-none",
            isActive && "bg-primary/10! text-primary! [&_svg]:text-primary!",
          )}
        >
          <Link
            href={href}
            onDragStartCapture={(e) => {
              if (!isReorderEnabled) {
                return;
              }
              e.preventDefault();
            }}
            style={{
              pointerEvents: isDragging ? "none" : "auto",
              cursor: isDragging ? "grabbing" : "pointer",
            }}
          >
            {renderViewContent(item)}
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  },
  // カスタム比較関数で再レンダリングを最小化
  (prevProps, nextProps) => {
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.href === nextProps.href &&
      prevProps.pathname === nextProps.pathname &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.isReorderEnabled === nextProps.isReorderEnabled &&
      prevProps.className === nextProps.className
    );
  },
);
